import { useEffect, useReducer, useRef, useState } from "react";
import { useAction } from "convex/react";
import { Loader2, Mic, Square, X } from "lucide-react";
import { api } from "../../../convex/_generated/api";
import { cn } from "../../lib/cn";
import { useLoggingFlow } from "../../lib/logging-flow";
import type { LoggingFlowPayload } from "../../lib/logging-flow";
import {
  VOICE_RECORDING_ERROR_MESSAGES,
  encodeBlobAsBase64,
  startVoiceRecording,
  transcribeAudioRef,
} from "../../lib/voice-recorder";
import type {
  ActiveVoiceRecording,
  TranscribeAudioArgs,
  TranscribeAudioResult,
  VoiceRecordingStartError,
} from "../../lib/voice-recorder";
import { CreditDrawer } from "../CreditDrawer";
import { mapAnalysisItems, runMealTextAnalysis } from "./TextLogFlow";
import type { MealTextAnalysis } from "./TextLogFlow";
import type { MealReviewPayload } from "../meal-review/review-utils";

// Voice logging flow (plan todo 15): record via the shared voice-recorder
// (todo 13) -> transcribe (convex audio.transcribeAudio) -> EDITABLE
// transcript (the user corrects mistakes) -> "Analyze" ->
// api.analyze.analyzeMealText -> unified Meal Review with source "voice".
// This standalone flow ALWAYS shows the transcript for correction — the
// autoSendVoice setting is respected only inside Chat and is intentionally
// NOT read here. The flow NEVER logs or sends without the review screen.
// Mic-permission denial shows inline guidance plus a "Type instead" fallback
// (startFlow("text", { note: transcript-if-any })); transcription failure
// shows an inline error with Retry (the retained audio) and Type instead.
// INSUFFICIENT_CREDITS opens the CreditDrawer; ANALYSIS_FAILED offers
// Retry / Enter manually (blank review), mirroring TextLogFlow/PhotoLogFlow.

// ---------------------------------------------------------------------------
// Pure logic (the interactivity seam for the repo's node-env test pattern:
// renderToStaticMarkup + pure functions, NO jsdom).
// ---------------------------------------------------------------------------

export type VoiceFlowPhase =
  | "ready" // initial: big record button + guidance
  | "recording" // mic live: elapsed ticker + pulse + stop/cancel
  | "transcribing" // audio captured, transcription action in flight
  | "transcript" // editable transcript + Analyze (the mandatory correction step)
  | "analyzing" // analyzeMealText in flight (transcript stays visible, disabled)
  | "analysis-error" // ANALYSIS_FAILED/unknown: Retry / Enter manually
  | "mic-error" // typed start failure: guidance + Try again / Type instead
  | "transcribe-error"; // transcription failure: inline error + Retry / Type instead

export interface VoiceFlowState {
  readonly phase: VoiceFlowPhase;
  readonly transcript: string;
  readonly mimeType: string | null;
  readonly recordingStartedAt: number | null;
  // Retained after a failed transcription so Retry needs no second recording.
  readonly audioBase64: string | null;
  readonly errorMessage: string | null;
  readonly micError: VoiceRecordingStartError | null;
  readonly creditDrawerOpen: boolean;
}

export const INITIAL_VOICE_FLOW_STATE: VoiceFlowState = {
  phase: "ready",
  transcript: "",
  mimeType: null,
  recordingStartedAt: null,
  audioBase64: null,
  errorMessage: null,
  micError: null,
  creditDrawerOpen: false,
};

export type VoiceFlowAction =
  | { readonly type: "recordingStarted"; readonly mimeType: string; readonly startedAt: number }
  | { readonly type: "recordingCancelled" }
  | { readonly type: "recordingFailed"; readonly error: VoiceRecordingStartError }
  | { readonly type: "recorderError"; readonly message: string }
  | { readonly type: "recordingStopped" }
  | { readonly type: "audioEncoded"; readonly audioBase64: string }
  | { readonly type: "transcribeSucceeded"; readonly text: string }
  | { readonly type: "transcribeFailed"; readonly message: string }
  | { readonly type: "retryTranscription" }
  | { readonly type: "transcriptChanged"; readonly transcript: string }
  | { readonly type: "analyzeStarted" }
  | { readonly type: "analyzeFailed"; readonly message: string }
  | { readonly type: "analyzeFailedCredits" }
  | { readonly type: "creditDrawerClosed" };

export function voiceFlowReducer(
  state: VoiceFlowState,
  action: VoiceFlowAction,
): VoiceFlowState {
  switch (action.type) {
    case "recordingStarted":
      if (state.phase !== "ready" && state.phase !== "mic-error") return state;
      return {
        ...state,
        phase: "recording",
        mimeType: action.mimeType,
        recordingStartedAt: action.startedAt,
        errorMessage: null,
        micError: null,
      };
    case "recordingCancelled":
      if (state.phase !== "recording") return state;
      return {
        ...state,
        phase: "ready",
        mimeType: null,
        recordingStartedAt: null,
        audioBase64: null,
        errorMessage: null,
      };
    case "recordingFailed":
      return {
        ...state,
        phase: "mic-error",
        micError: action.error,
        recordingStartedAt: null,
        errorMessage: null,
      };
    case "recorderError":
      // Mid-recording MediaRecorder failure: no audio survives, so Retry
      // transcription is impossible — only Type instead remains.
      if (state.phase !== "recording") return state;
      return {
        ...state,
        phase: "transcribe-error",
        recordingStartedAt: null,
        audioBase64: null,
        errorMessage: action.message,
      };
    case "recordingStopped":
      if (state.phase !== "recording") return state;
      return { ...state, phase: "transcribing", recordingStartedAt: null, errorMessage: null };
    case "audioEncoded":
      if (state.phase !== "transcribing") return state;
      return { ...state, audioBase64: action.audioBase64 };
    case "transcribeSucceeded":
      if (state.phase !== "transcribing") return state;
      return { ...state, phase: "transcript", transcript: action.text, errorMessage: null };
    case "transcribeFailed":
      if (state.phase !== "transcribing") return state;
      return { ...state, phase: "transcribe-error", errorMessage: action.message };
    case "retryTranscription":
      if (state.phase !== "transcribe-error" || state.audioBase64 === null) return state;
      return { ...state, phase: "transcribing", errorMessage: null };
    case "transcriptChanged":
      return { ...state, transcript: action.transcript, errorMessage: null };
    case "analyzeStarted":
      if (
        (state.phase !== "transcript" && state.phase !== "analysis-error") ||
        state.transcript.trim().length === 0
      ) {
        return state;
      }
      return { ...state, phase: "analyzing", errorMessage: null };
    case "analyzeFailed":
      if (state.phase !== "analyzing") return state;
      return { ...state, phase: "analysis-error", errorMessage: action.message };
    case "analyzeFailedCredits":
      // Credits are recoverable in place: back to the transcript step with
      // the drawer open; nothing the user corrected is lost.
      if (state.phase !== "analyzing") return state;
      return { ...state, phase: "transcript", creditDrawerOpen: true, errorMessage: null };
    case "creditDrawerClosed":
      return { ...state, creditDrawerOpen: false };
  }
}

// Recording elapsed ticker label ("0:00" -> "1:05"); clamps negatives.
export function formatElapsedMs(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// "Type instead" fallback payload (todo 15 contract): the corrected/partial
// transcript rides into the text flow as its initial note; no transcript
// means a bare startFlow("text") with no payload.
export function buildTypeInsteadPayload(transcript: string): LoggingFlowPayload | undefined {
  const note = transcript.trim();
  return note.length > 0 ? { note } : undefined;
}

// Review payload contract (todo 12 MealReviewPayload) with source "voice" —
// the voice source enables hidden-ingredient auto-expand for
// balanced/accurate logging styles. Nullable analysis fields become absent
// review fields; unknown nutrition stays unknown, never zero-filled.
export function buildVoiceReviewPayload(analysis: MealTextAnalysis): MealReviewPayload {
  const { hasRange, lowKcal, highKcal, reason } = analysis.uncertainty;
  const uncertainty =
    hasRange && lowKcal !== null && highKcal !== null
      ? { lowKcal, highKcal, reason: reason ?? "The AI gave a calorie range for this meal." }
      : undefined;
  return {
    items: mapAnalysisItems(analysis),
    source: "voice",
    ...(analysis.mealName === null ? {} : { name: analysis.mealName }),
    ...(analysis.mealTypeGuess === null ? {} : { mealType: analysis.mealTypeGuess }),
    ...(uncertainty === undefined ? {} : { uncertainty }),
  };
}

export type TranscribeAudioFn = (args: TranscribeAudioArgs) => Promise<TranscribeAudioResult>;

export type VoiceTranscriptionOutcome =
  | { readonly ok: true; readonly text: string }
  | { readonly ok: false; readonly message: string };

// Pure controller around the transcription action (todo-21 pattern): the
// component only maps the outcome onto reducer dispatches, so tests exercise
// both branches with a mocked transcribe function.
export async function runVoiceTranscription(
  transcribe: TranscribeAudioFn,
  input: { readonly audioBase64: string; readonly mimeType: string },
): Promise<VoiceTranscriptionOutcome> {
  try {
    const result = await transcribe({ audioBase64: input.audioBase64, mimeType: input.mimeType });
    const text = result.text.trim();
    console.log("[Frontend][VoiceLogFlow] Voice transcription completed", {
      model: result.model,
      language: result.language,
      textLength: text.length,
    });
    return { ok: true, text };
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.error("[Frontend][VoiceLogFlow] Voice transcription failed", { error: message });
    return { ok: false, message };
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface VoiceLogFlowProps {
  readonly demoMode: boolean;
  readonly onClose: () => void;
  // Test hook: lets renderToStaticMarkup suites render a specific step.
  readonly initialState?: VoiceFlowState;
}

const MIC_GUIDANCE: Record<VoiceRecordingStartError, string> = {
  unsupported: "Your browser or WebView cannot record audio. You can still type your meal instead.",
  "no-supported-mime":
    "Your browser cannot encode a supported audio format. You can still type your meal instead.",
  "mic-permission":
    "Allow microphone access for this app in your browser or device settings, then try again, or type your meal instead.",
};

export function VoiceLogFlow({ demoMode, onClose, initialState }: VoiceLogFlowProps) {
  const [state, dispatch] = useReducer(
    voiceFlowReducer,
    initialState ?? INITIAL_VOICE_FLOW_STATE,
  );
  const { startFlow } = useLoggingFlow();
  const transcribeAction = useAction(transcribeAudioRef);
  const analyzeAction = useAction(api.analyze.analyzeMealText);
  const activeRecordingRef = useRef<ActiveVoiceRecording | null>(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  // Full MediaRecorder cleanup on unmount: cancel() detaches handlers first
  // and emits NOTHING, so no blob can arrive after the sheet is gone.
  useEffect(() => {
    return () => {
      activeRecordingRef.current?.cancel();
      activeRecordingRef.current = null;
    };
  }, []);

  // Elapsed ticker while the mic is live (SSR never runs effects: "0:00").
  useEffect(() => {
    if (state.phase !== "recording" || state.recordingStartedAt === null) return;
    const startedAt = state.recordingStartedAt;
    setElapsedMs(Date.now() - startedAt);
    const interval = setInterval(() => setElapsedMs(Date.now() - startedAt), 500);
    return () => clearInterval(interval);
  }, [state.phase, state.recordingStartedAt]);

  const runTranscription = async (audioBase64: string, mimeType: string) => {
    const outcome = await runVoiceTranscription(transcribeAction, { audioBase64, mimeType });
    if (outcome.ok) {
      dispatch({ type: "transcribeSucceeded", text: outcome.text });
      return;
    }
    dispatch({ type: "transcribeFailed", message: outcome.message });
  };

  // stop() emits the assembled blob exactly once via this callback; encode,
  // then transcribe. Encoding failure maps to the transcription error state.
  const handleRecordingBlob = async (blob: Blob) => {
    activeRecordingRef.current = null;
    dispatch({ type: "recordingStopped" });
    const mimeType = blob.type || "audio/webm";
    let audioBase64: string;
    try {
      audioBase64 = await encodeBlobAsBase64(blob);
    } catch (error: unknown) {
      console.error("[Frontend][VoiceLogFlow] Audio encoding failed", {
        error: error instanceof Error ? error.message : String(error),
      });
      dispatch({ type: "transcribeFailed", message: "Could not prepare the recording." });
      return;
    }
    dispatch({ type: "audioEncoded", audioBase64 });
    console.log("[Frontend][VoiceLogFlow] Recording captured; transcribing", {
      mimeType,
      blobSize: blob.size,
    });
    await runTranscription(audioBase64, mimeType);
  };

  const handleStartRecording = async () => {
    if (demoMode || state.phase === "recording") return;
    console.log("[Frontend][VoiceLogFlow] Recording start requested");
    const result = await startVoiceRecording({
      onBlob: (blob) => {
        void handleRecordingBlob(blob);
      },
      onRecorderError: () =>
        dispatch({ type: "recorderError", message: "Recording failed. Please try again." }),
    });
    if (!result.ok) {
      console.warn("[Frontend][VoiceLogFlow] Recording start failed", { error: result.error });
      dispatch({ type: "recordingFailed", error: result.error });
      return;
    }
    activeRecordingRef.current = result.recording;
    dispatch({
      type: "recordingStarted",
      mimeType: result.recording.mimeType,
      startedAt: Date.now(),
    });
  };

  const handleStopRecording = () => {
    // onBlob drives the transcribing transition; stop() keeps the blob.
    activeRecordingRef.current?.stop();
  };

  const handleCancelRecording = () => {
    activeRecordingRef.current?.cancel();
    activeRecordingRef.current = null;
    dispatch({ type: "recordingCancelled" });
  };

  const handleClose = () => {
    activeRecordingRef.current?.cancel();
    activeRecordingRef.current = null;
    onClose();
  };

  const handleRetryTranscription = () => {
    if (state.audioBase64 === null) return;
    dispatch({ type: "retryTranscription" });
    void runTranscription(state.audioBase64, state.mimeType ?? "audio/webm");
  };

  const handleAnalyze = async () => {
    const description = state.transcript.trim();
    if (description.length === 0 || state.phase === "analyzing") return;
    dispatch({ type: "analyzeStarted" });
    console.log("[Frontend][VoiceLogFlow] Transcript analysis requested", {
      descriptionLength: description.length,
    });
    const outcome = await runMealTextAnalysis(analyzeAction, { description });
    if (outcome.status === "analyzed") {
      const payload = buildVoiceReviewPayload(outcome.analysis);
      console.log("[Frontend][VoiceLogFlow] Routing voice analysis to review", {
        itemCount: payload.items?.length ?? 0,
        hasUncertainty: payload.uncertainty !== undefined,
      });
      startFlow("review", payload);
      return;
    }
    if (outcome.kind === "credits") {
      dispatch({ type: "analyzeFailedCredits" });
      return;
    }
    dispatch({ type: "analyzeFailed", message: "Meal analysis failed" });
  };

  const handleTypeInstead = () => {
    const payload = buildTypeInsteadPayload(state.transcript);
    console.log("[Frontend][VoiceLogFlow] Falling back to text flow", {
      hasTranscript: payload !== undefined,
    });
    if (payload === undefined) {
      startFlow("text");
    } else {
      startFlow("text", payload);
    }
  };

  const handleEnterManually = () => {
    console.log("[Frontend][VoiceLogFlow] Analysis declined; opening blank review");
    startFlow("review", { items: [], source: "voice" });
  };

  const primaryButtonClass =
    "inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-base font-medium text-white transition-all duration-200 ease-out active:bg-accent-dark active:scale-[0.98] select-none disabled:opacity-50 disabled:cursor-not-allowed";
  const secondaryButtonClass =
    "inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-3 text-base font-medium text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none";

  let body;
  if (demoMode) {
    // Demo mode has no backend/auth: no dead record/analyze buttons, just the
    // manual path into the (fully local) review screen.
    body = (
      <>
        <div data-testid="voice-log-demo-note" className="rounded-xl border border-border bg-muted p-4">
          <p className="text-sm font-medium text-text">Voice logging needs a signed-in account</p>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">
            Demo mode cannot record or transcribe audio, but you can open the review and enter the meal yourself.
          </p>
        </div>
        <button
          type="button"
          data-testid="voice-log-enter-manual"
          onClick={handleEnterManually}
          className={cn(primaryButtonClass, "mt-4")}
        >
          Enter manually
        </button>
      </>
    );
  } else if (state.phase === "recording") {
    body = (
      <div data-testid="voice-log-recording" className="rounded-xl border border-border bg-muted p-4">
        <div className="flex items-center gap-3">
          <span
            data-testid="voice-log-pulse"
            className="h-3 w-3 shrink-0 animate-pulse rounded-full bg-danger"
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-text">Recording…</p>
            <p className="text-xs text-text-muted">Describe your meal, then tap stop.</p>
          </div>
          <span data-testid="voice-log-elapsed" className="shrink-0 text-sm font-semibold tabular-nums text-text">
            {formatElapsedMs(elapsedMs)}
          </span>
        </div>
        <button
          type="button"
          data-testid="voice-log-stop"
          onClick={handleStopRecording}
          className={cn(primaryButtonClass, "mt-4")}
        >
          <Square size={16} fill="currentColor" aria-hidden="true" />
          Stop recording
        </button>
        <button
          type="button"
          data-testid="voice-log-cancel"
          onClick={handleCancelRecording}
          className={cn(secondaryButtonClass, "mt-2")}
        >
          Cancel
        </button>
      </div>
    );
  } else if (state.phase === "transcribing") {
    body = (
      <div
        data-testid="voice-log-transcribing"
        role="status"
        className="flex items-center gap-3 rounded-xl border border-border bg-muted p-4"
      >
        <Loader2 size={22} className="shrink-0 animate-spin text-accent" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-text">Transcribing your voice…</p>
          <p className="text-xs text-text-muted">You will review the transcript before anything is analyzed.</p>
        </div>
      </div>
    );
  } else if (state.phase === "mic-error" && state.micError !== null) {
    body = (
      <div data-testid="voice-log-mic-error" role="alert" className="rounded-xl border border-border bg-muted p-4">
        <p className="text-sm font-medium text-danger">{VOICE_RECORDING_ERROR_MESSAGES[state.micError]}</p>
        <p className="mt-1 text-xs leading-relaxed text-text-muted">{MIC_GUIDANCE[state.micError]}</p>
        <div className="mt-3 flex flex-col gap-2">
          <button
            type="button"
            data-testid="voice-log-mic-retry"
            onClick={() => void handleStartRecording()}
            className={primaryButtonClass}
          >
            Try again
          </button>
          <button
            type="button"
            data-testid="voice-log-type-instead"
            onClick={handleTypeInstead}
            className={secondaryButtonClass}
          >
            Type instead
          </button>
        </div>
      </div>
    );
  } else if (state.phase === "transcribe-error") {
    body = (
      <div data-testid="voice-log-transcribe-error" role="alert" className="rounded-xl border border-border bg-muted p-4">
        <p className="text-sm font-medium text-danger">We could not transcribe that recording</p>
        <p className="mt-1 text-xs leading-relaxed text-text-muted">
          Check your connection and try again, or type the meal yourself.
        </p>
        <div className="mt-3 flex flex-col gap-2">
          {state.audioBase64 !== null && (
            <button
              type="button"
              data-testid="voice-log-retry-transcription"
              onClick={handleRetryTranscription}
              className={primaryButtonClass}
            >
              Retry transcription
            </button>
          )}
          <button
            type="button"
            data-testid="voice-log-type-instead"
            onClick={handleTypeInstead}
            className={secondaryButtonClass}
          >
            Type instead
          </button>
        </div>
      </div>
    );
  } else if (state.phase === "transcript" || state.phase === "analyzing" || state.phase === "analysis-error") {
    const analyzing = state.phase === "analyzing";
    body = (
      <>
        <label htmlFor="voice-log-transcript" className="block text-sm font-semibold text-text">
          Your transcript{" "}
          <span className="font-normal text-text-muted">(correct anything that was misheard)</span>
        </label>
        <textarea
          id="voice-log-transcript"
          data-testid="voice-log-transcript"
          value={state.transcript}
          onChange={(event) => dispatch({ type: "transcriptChanged", transcript: event.target.value })}
          rows={4}
          placeholder="e.g. chai with two sugars"
          disabled={analyzing}
          className={cn(
            "mt-2 w-full resize-none rounded-xl border border-border bg-muted px-4 py-3 text-base",
            "text-text placeholder:text-text-muted caret-accent",
            "focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent",
            "disabled:opacity-50 disabled:cursor-not-allowed",
            "min-h-[96px]",
          )}
        />
        {state.phase === "analysis-error" && (
          <div data-testid="voice-log-error" role="alert" className="mt-3 rounded-xl border border-border bg-muted p-4">
            <p className="text-sm font-medium text-danger">We could not analyze that transcript</p>
            <p className="mt-1 text-xs leading-relaxed text-text-muted">
              Check your connection and try again, or enter the meal yourself.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                data-testid="voice-log-retry-analysis"
                onClick={() => void handleAnalyze()}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
              >
                Retry
              </button>
              <button
                type="button"
                data-testid="voice-log-enter-manual"
                onClick={handleEnterManually}
                className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-xl border border-border bg-surface px-4 py-2 text-sm font-semibold text-text transition-all duration-200 ease-out active:bg-muted active:scale-[0.98] select-none"
              >
                Enter manually
              </button>
            </div>
          </div>
        )}
        <button
          type="button"
          data-testid="voice-log-analyze"
          onClick={() => void handleAnalyze()}
          disabled={state.transcript.trim().length === 0 || analyzing}
          aria-busy={analyzing}
          className={cn(primaryButtonClass, "mt-4")}
        >
          {analyzing && <Loader2 size={18} className="animate-spin" aria-hidden="true" />}
          {analyzing ? "Analyzing..." : "Analyze"}
        </button>
      </>
    );
  } else {
    // "ready" — the record entry point.
    body = (
      <>
        <div className="rounded-xl border border-border bg-muted p-4">
          <p className="text-sm font-medium text-text">Describe your meal out loud</p>
          <p className="mt-1 text-xs leading-relaxed text-text-muted">
            For example: "chai with two sugars". You will review and correct the transcript before
            anything is analyzed or logged.
          </p>
        </div>
        <button
          type="button"
          data-testid="voice-log-record"
          onClick={() => void handleStartRecording()}
          className={cn(primaryButtonClass, "mt-4 min-h-[56px]")}
        >
          <Mic size={20} aria-hidden="true" />
          Start recording
        </button>
        <button
          type="button"
          data-testid="voice-log-type-instead"
          onClick={handleTypeInstead}
          className="mt-2 inline-flex min-h-[44px] w-full items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-medium text-text-muted transition-opacity active:opacity-80 select-none"
        >
          Prefer typing? Type instead
        </button>
      </>
    );
  }

  return (
    <>
      <div
        data-testid="voice-log-scrim"
        className="fixed inset-0 z-40 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex max-h-[90vh] flex-col justify-end p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
        <div className="pointer-events-auto mx-auto w-full max-w-xl overflow-y-auto rounded-2xl bg-surface ob-sheet-up" data-testid="voice-log-flow">
          <div className="sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-2xl border-b border-border bg-surface px-5 py-2">
            <h3 className="text-lg font-semibold text-text">Voice log your meal</h3>
            <button
              type="button"
              data-testid="voice-log-close"
              aria-label="Close flow"
              className="flex h-11 w-11 items-center justify-center rounded-full text-text-muted active:bg-muted active:scale-[0.98] select-none"
              onClick={handleClose}
            >
              <X size={20} strokeWidth={2} />
            </button>
          </div>
          <div className="px-5 pb-6 pt-4">{body}</div>
        </div>
      </div>
      {state.creditDrawerOpen && (
        <CreditDrawer onClose={() => dispatch({ type: "creditDrawerClosed" })} />
      )}
    </>
  );
}

export default VoiceLogFlow;
